import moment from 'moment-timezone';
import Moment = moment.Moment;
import { LegFiJwtService } from '../../services/auth/legfi-jwt.service';
import { BaseAvatar } from './base/avatar';
import { SimpleOrganization } from '../../services/organization/organization.service';

/**
 * SD-9537: the two queues a specialist can serve in. Mirrors `AssignedSalesRep::QUEUE_*` in core,
 * which is what the reassignment endpoint validates against.
 *
 * EVERY specialist serves in the onboarding queue — that is what an onboarding specialist IS, so
 * there is no membership flag and no toggle for it; `excludeFromLeads` governs whether they receive
 * signup leads. The BDR queue is the one opt-in role.
 */
export enum RepQueue {
    ONBOARDING = 'onboarding',
    BDR = 'bdr',
}

/**
 * SD-9097: how many organizations a rep holds — SD-9537: in a NAMED queue.
 *
 * `organizationsCount` counts ONLY Active Rep assignments (`organizations.assigned_sales_rep_id`)
 * and `bdrOrganizationsCount` only BDR ones. Every consumer that asks "does this rep hold anything"
 * must go through here — reading the raw count sent BDRs down the "holds nothing" path, which both
 * deleted them without reassigning (stranding their orgs) and permanently disabled their Lead
 * Assignments menu item.
 *
 * The queue is now a PARAMETER rather than read off `rep.isBdr`: a rep can be in both queues at
 * once, so the flag no longer says which set of assignments a caller means. Callers pass the queue
 * whose list they are rendering.
 *
 * A free function rather than a getter on the class on purpose: this area's specs build reps as
 * type-cast object literals, not real instances, so a getter would be `undefined` in every one.
 */
export function organizationsHeldBy(rep: AssignedSalesRep, queue: RepQueue): number {
    return (queue === RepQueue.BDR ? rep.bdrOrganizationsCount : rep.organizationsCount) || 0;
}

/**
 * Whether this rep serves in the given queue at all (active or paused).
 *
 * Always true for the onboarding queue: every specialist is an Active Rep.
 */
export function isInQueue(rep: AssignedSalesRep, queue: RepQueue): boolean {
    return queue === RepQueue.BDR ? !!rep.isBdr : true;
}

/**
 * Whether this rep is paused in the given queue.
 *
 * SD-9537: each queue carries its own pause flag, which is the whole point — a specialist serving
 * actively as a BDR while paused as an Active Rep is the state the team needs, and one shared flag
 * could not express it.
 */
export function isPausedInQueue(rep: AssignedSalesRep, queue: RepQueue): boolean {
    return !!(queue === RepQueue.BDR ? rep.bdrExcludeFromLeads : rep.excludeFromLeads);
}

/**
 * SD-9990: a BDR paused in the Active Rep queue is paused BY DESIGN — they hold leads there without
 * drawing new ones from the round-robin. That is their normal resting state, so choosing them as a
 * target earns a neutral notice, not a warning.
 *
 * Scoped to the Active Rep queue on purpose (review finding F-5bc5): a BDR paused in the BDR queue
 * has been deliberately paused as a BDR, which is exactly what the warning exists to surface.
 */
export function isPausedByDesign(rep: AssignedSalesRep, queue: RepQueue): boolean {
    return queue === RepQueue.ONBOARDING && !!rep.isBdr && isPausedInQueue(rep, queue);
}

/**
 * SD-9990: whether choosing this rep as a reassignment target deserves a "paused" warning.
 *
 * A paused rep is a valid target — the pause governs what the round-robin hands out, not what an
 * admin may deliberately assign — but a deliberate pause is worth saying out loud. The one
 * exception is the by-design pause above.
 */
export function warnsAsPausedTarget(rep: AssignedSalesRep, queue: RepQueue): boolean {
    return isPausedInQueue(rep, queue) && !isPausedByDesign(rep, queue);
}

/**
 * The organizations this rep holds in a named queue. `show()` returns both lists; which one a
 * caller wants depends on the queue they are looking at, not on `isBdr`.
 */
export function organizationsInQueue(rep: AssignedSalesRep, queue: RepQueue): SimpleOrganization[] {
    return (queue === RepQueue.BDR ? rep.bdrOrganizations : rep.organizations) ?? [];
}

/** The queue's last-lead timestamp — each queue rotates on its own column (SD-9537). */
export function lastLeadInQueue(rep: AssignedSalesRep, queue: RepQueue): Moment {
    return queue === RepQueue.BDR ? rep.bdrLastLead : rep.lastLead;
}

export class AssignedSalesRep extends BaseAvatar {
    id: number;

    /** The rep's USER id — what organizations.assigned_sales_rep_id / assigned_bdr_rep_id store. */
    userId: number;

    email: string;
    phone: string;
    meetingLink: string;
    secondaryMeetingLink: string;

    lastLead: Moment;
    excludeFromLeads: boolean;
    isBdr: boolean;

    // SD-9537: the BDR role is ADDITIVE and carries its own pause flag and rotation timestamp.
    // `isBdr` kept its name but now means only "ALSO serves as a BDR" — it says nothing about
    // whether they are an Active Rep, because every specialist is one.
    bdrExcludeFromLeads: boolean;
    bdrLastLead: Moment;
    weight: number;
    skipStart: Moment;
    skipEnd: Moment;
    lastSkipped: Moment;
    organizations: SimpleOrganization[];
    organizationsCount: number;

    // SD-9097: the orgs this rep holds as their BDR. A separate pair from the two above, because
    // the assignments are separate columns server-side — a BDR's orgs never appear in
    // `organizations`/`organizationsCount`, which is why the delete guard could not see them.
    bdrOrganizations: SimpleOrganization[];
    bdrOrganizationsCount: number;

    constructor(data: any) {
        super(data);

        if (data.id) {
            this.id = data.id;
        }

        if (data.userId) {
            this.userId = data.userId;
        }

        const timezone = LegFiJwtService.getTimezone();
        this.email = data.email ?? data.user?.email ?? 'onboarding@payhoa.com';

        // avatar already sets name, unless it's null
        if (data.user && data.user.memberships) {
            const membership = data.user.memberships[0];
            this.name = membership.profile.givenNames + (membership.profile.familyName ? ' ' + membership.profile.familyName : '');
        }
        this.name = this.name ?? 'Support';

        this.meetingLink = data.meetingLink ?? 'https://meetings.hubspot.com/payhoa/45-minute-launchpad-demo';
        this.secondaryMeetingLink = data.secondaryMeetingLink ?? 'https://meetings.hubspot.com/payhoa/30-minute-launchpad-round-robin';

        if (data.phone) {
            this.phone = data.phone;
        }

        if (data.lastLead) {
            this.lastLead = moment.utc(data.lastLead, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }

        // SD-9097: store() and update() both return the raw Eloquent model (snake_case), while
        // index() and show() hand-build camelCase arrays — so these flags arrive under either
        // spelling depending on which call produced the response. Read both, or they are
        // undefined immediately after a create or a save.
        if (data.hasOwnProperty('excludeFromLeads') || data.hasOwnProperty('exclude_from_leads')) {
            this.excludeFromLeads = data.excludeFromLeads ?? data.exclude_from_leads;
        }

        if (data.hasOwnProperty('isBdr') || data.hasOwnProperty('is_bdr')) {
            this.isBdr = data.isBdr ?? data.is_bdr;
        }

        // SD-9537: same dual-spelling read as the two flags above, for the same reason — store()
        // and update() return the raw Eloquent model (snake_case) while index()/show() hand-build
        // camelCase, so these arrive under either spelling depending on the call.
        if (data.hasOwnProperty('bdrExcludeFromLeads') || data.hasOwnProperty('bdr_exclude_from_leads')) {
            this.bdrExcludeFromLeads = data.bdrExcludeFromLeads ?? data.bdr_exclude_from_leads;
        }

        if (data.bdrLastLead || data.bdr_last_lead) {
            this.bdrLastLead = moment.utc(data.bdrLastLead ?? data.bdr_last_lead, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }

        if (data.weight) {
            this.weight = 1 / data.weight;
        }

        if (data.skipStart) {
            this.skipStart = moment.utc(data.skipStart, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }

        if (data.skipEnd) {
            this.skipEnd = moment.utc(data.skipEnd, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }

        if (data.lastSkipped) {
            this.lastSkipped = moment.utc(data.lastSkipped, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }

        if (data.organizations) {
            this.organizations = data.organizations.map((org: Object) => new SimpleOrganization(org));
        }

        if (data.organizationsCount) {
            this.organizationsCount = data.organizationsCount;
        }

        if (data.bdrOrganizations) {
            this.bdrOrganizations = data.bdrOrganizations.map((org: Object) => new SimpleOrganization(org));
        }

        if (data.bdrOrganizationsCount) {
            this.bdrOrganizationsCount = data.bdrOrganizationsCount;
        }
    }

    get description() {
        return this.name !== 'Support' ? 'Dedicated Onboarding Specialist' : 'PayHOA Onboarding Team';
    }
}
